import mixpanel from "mixpanel-browser"
import { newArchBaseUrl } from "./constants"
import { extendedAppUrl, newBaseUrl, walletBaseUrl } from "./newbaseurl"
export const mixPanelKey = process.env.REACT_APP_MIX_PANEL_TOKEN as string
export const isMixpanelEnabled = Boolean(mixPanelKey)
const isDebug = process.env.NODE_ENV !== "production"
let initialized = false

const SENSITIVE_URL_KEYWORDS = [
  "auth",
  "login",
  "signin",
  "password",
  "otp",
  "2fa",
  "pin",
  "token",
  "session",
  "secret",
  "bvn",
  // Endpoints that send or return PII (phone, NIN, address, bank, salary)
  "people/select",
  "employees/get",
  "employees/view",
  "index_personal_info",
  "index_other_info",
  "index_compensation_info",
  "personalinfoforexternalloanrequest",
  "getforselectionwithaccountdetails",
  "update_personal_details",
  "update_nextofkin",
  "update_homeaddress",
  "update_tax",
  "update_pension",
  "update_preferred_account",
  "update_primary_account",
  "update_secondary_account",
  "import_account_details",
  "checkaccount",
  "payslip",
  "pfa",
  "benefits/view/pension",
  "loanrequest",
  "advancerequest",
  "/loans/",
  "kyc",
  "proof-of-residence",
  "basic-info",
  "resolve-account",
  "funding-account",
  "beneficiar",
  "/wallets",
  "/earnings/",
  "withdrawals",
  "synced-employees",
]

const API_BASE_URLS = [
  newArchBaseUrl,
  extendedAppUrl,
  newBaseUrl,
  walletBaseUrl,
]

const API_ORIGINS = Array.from(
  new Set(
    API_BASE_URLS.map((value) => {
      try {
        return new URL(String(value).trim()).origin
      } catch {
        return null
      }
    }).filter((origin): origin is string => Boolean(origin))
  )
)

const SENSITIVE_URL_GUARD = `(?!.*(${SENSITIVE_URL_KEYWORDS.join("|")}))`

const NETWORK_BODY_URL_PATTERNS = API_ORIGINS.map(
  (origin) =>
    `^${origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/${SENSITIVE_URL_GUARD}`
)

export const initMixpanel = () => {
  if (!isMixpanelEnabled) {
    if (isDebug)
      // eslint-disable-next-line no-console
      console.warn("[mixpanel] disabled — REACT_APP_MIX_PANEL_TOKEN is missing")
    return
  }
  if (initialized) return

  try {
    mixpanel.init(mixPanelKey, {
      debug: isDebug,
      persistence: "localStorage",
      record_sessions_percent: 0,
      record_idle_timeout_ms: 3_600_000,
      record_mask_all_text: false,
      record_mask_all_inputs: true,
      // Text is unmasked, so sensitive values must opt in with this class
      record_mask_text_selector: [".mp-mask"],
      record_heatmap_data: true,
      record_network: true,
      record_network_options: {
        // Without this allowlist the SDK captures no req bodies
        recordBodyUrls: {
          request: NETWORK_BODY_URL_PATTERNS,
          response: NETWORK_BODY_URL_PATTERNS,
        },
      },
    })

    initialized = true

    if (window.location.pathname.startsWith("/auth")) {
      mixpanel.start_session_recording()
    }

    if (isDebug) {
      ;(window as any).mixpanel = mixpanel
      // eslint-disable-next-line no-console
      console.info(
        "[mixpanel] initialized; session recording starts on identify"
      )
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[mixpanel] init failed", err)
  }
}
/** True while rrweb is actively recording a replay. */
const isRecordingActive = () => {
  const props = mixpanel.get_session_recording_properties() as {
    $mp_replay_id?: string
  }
  return Boolean(props && props.$mp_replay_id)
}

export interface MixpanelIdentity {
  email?: string | null
  fullName?: string | null
  company?: string | null
  companyId?: string | number | null
}

export const identifyMixpanelUser = ({
  email,
  fullName,
  company,
  companyId,
}: MixpanelIdentity) => {
  if (!isMixpanelEnabled || !email) return
  const previousDistinctId = String(mixpanel.get_distinct_id() ?? "")
  const wasMisattributed = previousDistinctId !== email
  const wasRecording = isRecordingActive()

  mixpanel.identify(email)

  mixpanel.people.set({
    $email: email,
    $name: fullName ?? undefined,
    Company: company ?? undefined,
    "Company Id": companyId ?? undefined,
  })

  mixpanel.register({
    Company: company ?? undefined,
    "Company Id": companyId ?? undefined,
  })

  const registerReplayUrl = () => {
    const replayUrl = mixpanel.get_session_replay_url()
    if (replayUrl) {
      mixpanel.register({ "Session Replay URL": replayUrl })
    }
  }

  if (wasRecording && wasMisattributed) {
    if (isDebug) {
      // eslint-disable-next-line no-console
      console.info(
        `[mixpanel] restarting replay: recording started as "${previousDistinctId}", now "${email}"`
      )
    }
    const stopped = mixpanel.stop_session_recording() as unknown as
      | Promise<unknown>
      | undefined
    Promise.resolve(stopped)
      .then(() => {
        mixpanel.start_session_recording()
        registerReplayUrl()
      })
      .catch(() => {
        // Recording is best-effort — never break sign-in over it.
      })
    return
  }

  mixpanel.start_session_recording()
  registerReplayUrl()
}

export const stopMixpanelSession = () => {
  if (!isMixpanelEnabled) return
  mixpanel.stop_session_recording()
  mixpanel.reset()
}

export const getSessionReplayUrl = () => {
  if (!isMixpanelEnabled) return ""
  return mixpanel.get_session_replay_url()
}
